import { DashboardFilters, TimeRange } from '@delivery-metrics/shared';
import {
  BookmarkBorder,
  Clear,
  ExpandLess,
  ExpandMore,
  FilterList,
  Save,
} from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
// Using native date inputs instead of MUI date pickers to avoid additional dependencies
import React, { useCallback, useEffect, useState } from 'react';

import { useShareableState } from '../hooks/useShareableState';
import { getCurrentUrlState, updateUrlState } from '../utils/shareableUrl';

import { DateRangePicker } from './DateRangePicker';

export interface FilterOption {
  value: string;
  label: string;
  count?: number;
}

export interface FilterConfig {
  key: string;
  label: string;
  type:
    'select' | 'multiselect' | 'autocomplete' | 'daterange' | 'text' | 'number';
  options?: FilterOption[];
  placeholder?: string;
  required?: boolean;
  width?: 'small' | 'medium' | 'large' | 'full';
  // Optional grouping key. Non-timeRange controls sharing a group render on the
  // same row; each distinct group renders on its own row, in first-seen order.
  group?: string;
}

export interface FilterPanelProps {
  filters: DashboardFilters;
  onFiltersChange: (filters: DashboardFilters) => void;
  filterConfigs?: FilterConfig[];
  showPresets?: boolean;
  showSaveFilter?: boolean;
  savedFilters?: Array<{ name: string; filters: DashboardFilters }>;
  onSaveFilter?: (name: string, filters: DashboardFilters) => void;
  onLoadFilter?: (filters: DashboardFilters) => void;
  onReset?: () => void;
  onApply?: () => void;
  applyDisabled?: boolean;
  loading?: boolean;
  compact?: boolean;
  collapsible?: boolean;
  defaultExpanded?: boolean;
  enableUrlSync?: boolean;
}

const TIME_RANGE_PRESETS = [
  { value: 'last30days', label: 'Last 30 Days' },
  { value: 'last90days', label: 'Last 90 Days' },
  { value: 'lastMonth', label: 'Last Month' },
  { value: 'last3months', label: 'Last 3 Months' },
  { value: 'last6months', label: 'Last 6 Months' },
  { value: 'lastQuarter', label: 'Last Quarter' },
  { value: 'ytd', label: 'YTD' },
  { value: 'lastYear', label: 'Last Year' },
];

const DATE_PRESETS = TIME_RANGE_PRESETS;

const DEFAULT_FILTER_CONFIGS: FilterConfig[] = [
  {
    key: 'timeRange',
    label: 'Time Range',
    type: 'daterange',
    required: true,
    width: 'large',
  },
  {
    key: 'teams',
    label: 'Teams',
    type: 'multiselect',
    width: 'medium',
  },
  {
    key: 'programs',
    label: 'Programs',
    type: 'multiselect',
    width: 'medium',
  },
  {
    key: 'repositories',
    label: 'Repositories',
    type: 'multiselect',
    width: 'medium',
  },
  {
    key: 'workItemTypes',
    label: 'Work Item Types',
    type: 'multiselect',
    options: [
      { value: 'Story', label: 'Story' },
      { value: 'Feature', label: 'Feature' },
      { value: 'Epic', label: 'Epic' },
      { value: 'Bug', label: 'Bug' },
      { value: 'Task', label: 'Task' },
    ],
    width: 'medium',
  },
];

export const FilterPanel: React.FC<FilterPanelProps> = ({
  filters,
  onFiltersChange,
  filterConfigs = DEFAULT_FILTER_CONFIGS,
  showPresets = true,
  showSaveFilter = false,
  savedFilters = [],
  onSaveFilter,
  onLoadFilter,
  onReset,
  onApply,
  applyDisabled = false,
  // loading = false, // Commented out as it's not used in current implementation
  compact = false,
  collapsible = false,
  defaultExpanded = true,
  enableUrlSync = false,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [saveFilterName, setSaveFilterName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const { viewState } = useShareableState();

  // Load filters from URL on component mount
  useEffect(() => {
    if (enableUrlSync) {
      const urlState = getCurrentUrlState();
      if (urlState && urlState.filters) {
        onFiltersChange(urlState.filters);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enableUrlSync]);

  // Update URL when filters change
  useEffect(() => {
    if (enableUrlSync && filters) {
      updateUrlState(filters, viewState);
    }
  }, [filters, viewState, enableUrlSync]);

  const handleFilterChange = useCallback(
    (key: string, value: any) => {
      const newFilters = { ...filters };

      if (key === 'timeRange.start' || key === 'timeRange.end') {
        const [, field] = key.split('.');
        newFilters.timeRange = {
          ...newFilters.timeRange,
          [field]: value,
          preset: 'custom',
        };
      } else {
        (newFilters as any)[key] = value;
      }

      onFiltersChange(newFilters);
    },
    [filters, onFiltersChange]
  );

  // Commented out unused functions for now - can be implemented when needed
  // const handleClearFilter = useCallback((key: string) => {
  //     const newFilters = { ...filters };
  //     delete (newFilters as any)[key];
  //     onFiltersChange(newFilters);
  // }, [filters, onFiltersChange]);

  const handleReset = useCallback(() => {
    // Default reset behavior - reset all filters
    const defaultTimeRange: TimeRange = {
      start: new Date(Date.UTC(new Date().getUTCFullYear(), 0, 1)),
      end: new Date(),
      preset: 'ytd',
    };

    const defaultFilters: DashboardFilters = {
      timeRange: defaultTimeRange,
      units: [],
      teams: [],
      repositories: [],
      programs: [],
    };

    // Always clear the draft
    onFiltersChange(defaultFilters);

    // Also reset applied filters if callback is provided
    if (onReset) {
      onReset();
    }
  }, [onReset, onFiltersChange]);

  const handleSaveFilter = useCallback(() => {
    if (saveFilterName.trim() && onSaveFilter) {
      onSaveFilter(saveFilterName.trim(), filters);
      setSaveFilterName('');
      setShowSaveDialog(false);
    }
  }, [saveFilterName, filters, onSaveFilter]);

  // const getFilterWidth = (width: FilterConfig['width']) => {
  //     switch (width) {
  //         case 'small':
  //             return { xs: 12, sm: 6, md: 3 };
  //         case 'medium':
  //             return { xs: 12, sm: 6, md: 4 };
  //         case 'large':
  //             return { xs: 12, sm: 8, md: 6 };
  //         case 'full':
  //             return { xs: 12 };
  //         default:
  //             return { xs: 12, sm: 6, md: 4 };
  //     }
  // };

  const renderFilterControl = (config: FilterConfig) => {
    const value = (filters as any)[config.key];

    switch (config.type) {
      case 'daterange':
        return (
          <DateRangePicker
            value={filters.timeRange}
            onChange={timeRange => {
              onFiltersChange({
                ...filters,
                timeRange,
              });
            }}
            showPresets={showPresets}
          />
        );

      case 'multiselect': {
        const options = config.options || [];
        const selectedValues: string[] = Array.isArray(value) ? value : [];
        // Represent every selected value as a chip, matching options
        // case-insensitively. A value that has no matching option (e.g. it is
        // valid but absent from the freshly scoped option list) is still shown
        // as selected via a synthesised option, so the displayed selection
        // never silently diverges from the stored state.
        const optionByValue = new Map(
          options.map(opt => [opt.value.toLowerCase(), opt])
        );
        const selectedOptions = selectedValues.map(
          val =>
            optionByValue.get(val.toLowerCase()) ?? { value: val, label: val }
        );
        return (
          <Autocomplete
            multiple
            options={options}
            getOptionLabel={option => option.label}
            isOptionEqualToValue={(opt, val) =>
              opt.value.toLowerCase() === val.value.toLowerCase()
            }
            value={selectedOptions}
            onChange={(_, newValue) => {
              handleFilterChange(
                config.key,
                newValue.map(v => v.value)
              );
            }}
            renderInput={params => (
              <TextField
                {...params}
                label={config.label}
                placeholder={config.placeholder}
                size='small'
                color='primary'
              />
            )}
            renderTags={(tagValue, getTagProps) =>
              tagValue.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option.value}
                  label={option.label}
                  size='small'
                  color='primary'
                />
              ))
            }
          />
        );
      }

      case 'select':
        return (
          <FormControl fullWidth size='small'>
            <InputLabel>{config.label}</InputLabel>
            <Select
              value={value || ''}
              label={config.label}
              onChange={e => handleFilterChange(config.key, e.target.value)}
            >
              {config.options?.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                  {option.count !== undefined && (
                    <Typography
                      variant='caption'
                      sx={{ ml: 1, color: 'text.secondary' }}
                    >
                      ({option.count})
                    </Typography>
                  )}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      case 'text':
        return (
          <TextField
            fullWidth
            size='small'
            label={config.label}
            placeholder={config.placeholder}
            value={value || ''}
            onChange={e => handleFilterChange(config.key, e.target.value)}
          />
        );

      case 'number':
        return (
          <TextField
            fullWidth
            size='small'
            type='number'
            label={config.label}
            placeholder={config.placeholder}
            value={value || ''}
            onChange={e =>
              handleFilterChange(config.key, Number(e.target.value))
            }
          />
        );

      default:
        return null;
    }
  };

  const getActiveFiltersSummary = () => {
    const summary: string[] = [];

    const hasTimeRangeFilter = filterConfigs.some(c => c.key === 'timeRange');

    if (hasTimeRangeFilter && filters.timeRange) {
      const preset = filters.timeRange.preset;
      if (preset && preset !== 'custom') {
        const presetLabel = DATE_PRESETS.find(p => p.value === preset)?.label;
        if (presetLabel) summary.push(`Date range: ${presetLabel}`);
      } else {
        summary.push(
          `Date range: ${filters.timeRange.start.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' })} - ${filters.timeRange.end.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' })}`
        );
      }
    }

    filterConfigs
      .filter(config => config.key !== 'timeRange')
      .forEach(config => {
        const value = (filters as any)[config.key];

        if (config.type === 'multiselect') {
          const values = Array.isArray(value) ? value : [];
          if (values.length > 0) {
            summary.push(`${config.label}: ${values.join(', ')}`);
          } else {
            summary.push(`${config.label}: All`);
          }
        }
      });

    return summary;
  };

  const content = (
    <CardContent
      sx={{
        p: compact ? 1 : 1.5,
        '&:last-child': {
          pb: compact ? 1 : 1.5,
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          mb: 0,
          cursor: collapsible ? 'pointer' : 'default',
          borderRadius: 1,
          '&:hover': collapsible ? { backgroundColor: 'action.selected' } : {},
          minHeight: '40px',
        }}
        onClick={collapsible ? () => setExpanded(!expanded) : undefined}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexWrap: 'wrap',
            flex: 1,
            minHeight: '40px',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterList fontSize='small' />
            <Typography variant='subtitle1' sx={{ fontWeight: 600 }}>
              Filters
            </Typography>
          </Box>
          {/* Show filter chips inline when collapsed */}
          {collapsible && !expanded && (
            <>
              {getActiveFiltersSummary().map((summary, index) => (
                <Chip
                  key={index}
                  label={summary}
                  size='small'
                  variant='outlined'
                  color='primary'
                />
              ))}
            </>
          )}
        </Box>
        <Box
          sx={{ display: 'flex', gap: 1, flexShrink: 0 }}
          onClick={e => e.stopPropagation()}
        >
          {showSaveFilter && (
            <Tooltip title='Save Filter'>
              <IconButton size='small' onClick={() => setShowSaveDialog(true)}>
                <BookmarkBorder />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title='Reset Filters'>
            <IconButton
              size='small'
              onClick={e => {
                e.stopPropagation();
                handleReset();
              }}
            >
              <Clear />
            </IconButton>
          </Tooltip>
          {collapsible && (
            <IconButton size='small' onClick={() => setExpanded(!expanded)}>
              {expanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          )}
        </Box>
      </Box>

      <Collapse in={expanded}>
        <Box sx={{ mt: 2 }}>
          {/* Date Range Picker - Full Width */}
          {filterConfigs.find(c => c.key === 'timeRange') && (
            <Box sx={{ mb: 3 }}>
              {renderFilterControl(
                filterConfigs.find(c => c.key === 'timeRange')!
              )}
            </Box>
          )}

          {/* Other Filters - Compact Grid, one row per group */}
          {(() => {
            const nonTimeConfigs = filterConfigs.filter(
              config => config.key !== 'timeRange'
            );
            const groupOrder: string[] = [];
            for (const config of nonTimeConfigs) {
              const groupKey = config.group ?? 'default';
              if (!groupOrder.includes(groupKey)) groupOrder.push(groupKey);
            }
            return groupOrder.map((groupKey, groupIndex) => (
              <Box
                key={groupKey}
                sx={{
                  display: 'grid',
                  gap: 2,
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  mt: groupIndex === 0 ? 0 : 2,
                }}
              >
                {nonTimeConfigs
                  .filter(config => (config.group ?? 'default') === groupKey)
                  .map(config => (
                    <Box key={config.key}>{renderFilterControl(config)}</Box>
                  ))}
              </Box>
            ));
          })()}

          {savedFilters.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Box>
                <Typography variant='subtitle2' gutterBottom>
                  Saved Filters
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {savedFilters.map((savedFilter, index) => (
                    <Chip
                      key={index}
                      label={savedFilter.name}
                      onClick={() => onLoadFilter?.(savedFilter.filters)}
                      variant='outlined'
                      size='small'
                    />
                  ))}
                </Box>
              </Box>
            </>
          )}

          {showSaveDialog && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
              }}
            >
              <Typography variant='subtitle2' gutterBottom>
                Save Current Filter
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField
                  size='small'
                  placeholder='Filter name'
                  value={saveFilterName}
                  onChange={e => setSaveFilterName(e.target.value)}
                  onKeyPress={e => e.key === 'Enter' && handleSaveFilter()}
                />
                <Button
                  size='small'
                  variant='contained'
                  onClick={handleSaveFilter}
                >
                  <Save sx={{ mr: 1 }} />
                  Save
                </Button>
                <Button size='small' onClick={() => setShowSaveDialog(false)}>
                  Cancel
                </Button>
              </Box>
            </Box>
          )}

          {onApply && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button
                variant='contained'
                onClick={() => {
                  onApply();
                  // 4623820: applying a selection dismisses the panel — the
                  // collapsed header chips already show what was applied, and
                  // leaving the panel open hides the filtered figures behind
                  // it.
                  if (collapsible) setExpanded(false);
                }}
                disabled={applyDisabled}
              >
                Apply Filters
              </Button>
            </Box>
          )}
        </Box>
      </Collapse>
    </CardContent>
  );

  if (compact) {
    return (
      <>
        <Box
          sx={{
            mb: 2,
            backgroundColor: 'action.hover',
            borderRadius: 1,
          }}
        >
          {content}
        </Box>
      </>
    );
  }

  return (
    <>
      <Card
        sx={{
          mb: 2,
          backgroundColor: 'action.hover',
        }}
      >
        {content}
      </Card>
    </>
  );
};
